import React, { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, UsersIcon, ClockIcon, SettingsIcon, BarChart3Icon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { trackEvent } from '../../utils/analytics';
import { PERMIS_IMAGES, permisCopy, permisBenefits, type Bi } from '../../data/permisHero';
import { QuestionCard, ProcessSteps, PropertyCard, VerifiedCard } from './PermisCards';
import { PermisPhone, PermisDashboard } from './PermisDevices';
const EASE = [0.23, 1, 0.32, 1] as const;
const BENEFIT_ICONS = {
  people: UsersIcon,
  clock: ClockIcon,
  gear: SettingsIcon,
  chart: BarChart3Icon
} as const;
export function PermisHero() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const pick = (v: Bi) => isFR ? v.fr : v.en;
  const caps = isFR ? permisCopy.capabilities.fr : permisCopy.capabilities.en;
  return <div className="relative max-w-[1640px] mx-auto" data-id="element-5083" data-mp="g20p9mobptoy">
      <div className="relative grid grid-cols-1 xl:grid-cols-[40fr_60fr] gap-12 xl:gap-4 items-center" data-id="element-5084" data-mp="p1tzm0c90aj8">
        {/* Left — message */}
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease: EASE
      }} className="relative z-10 min-w-0" data-id="element-5085" data-mp="k3s60lh74sti">
          <p className="font-sans text-[13px] font-semibold tracking-[0.3em] uppercase text-[#4F46E5]" data-id="element-5086" data-mp="b9njufd0c5kb">{pick(permisCopy.eyebrow)}</p>
          <h1 className="font-serif text-[2.7rem] sm:text-[3.6rem] 2xl:text-[4.4rem] leading-[1.02] tracking-tight mt-6" data-id="element-5087" data-mp="699bew7hutlr">
            <span className="block text-[#121A3A]" data-id="element-5088" data-mp="3siajj2pwp0ki">{pick(permisCopy.line1)}</span>
            <span className="block text-[#4F46E5]" data-id="element-5089" data-mp="3skdjaegv282f">{pick(permisCopy.line2)}</span>
            <span className="block text-[#4F46E5]" data-id="element-5090" data-mp="3oiry85u6anh5">{pick(permisCopy.line3)}</span>
            <span className="block text-[#4F46E5]" data-id="element-5091" data-mp="1qpw1m3nd3w43">{pick(permisCopy.line4)}</span>
          </h1>
          <p className="font-sans text-[17px] md:text-[19px] text-[#5B6478] leading-relaxed mt-7 max-w-[620px]" data-id="element-5092" data-mp="1mqx24ykegrk1">{pick(permisCopy.body)}</p>
          <div className="mt-9 flex flex-col sm:flex-row gap-3.5" data-id="element-5093" data-mp="3g6hvsaaw22pl">
            <Link to="/request-demo" onClick={() => trackEvent('Demo CTA Clicked', {
            location: 'Permis Hero'
          })} className="inline-flex items-center justify-center gap-3 rounded-xl bg-[#4F46E5] px-7 py-4 font-sans text-[17px] font-medium text-white shadow-[0_14px_30px_-14px_rgba(79,70,229,0.8)] transition-colors duration-150 hover:bg-[#4338CA] whitespace-nowrap" data-id="element-5094" data-mp="3l0m2qtk5maqo">
              {pick(permisCopy.primary)}
              <ArrowRightIcon className="w-5 h-5" strokeWidth={2} aria-hidden="true" data-id="element-5095" data-mp="3pynqhrsiyy8h" />
            </Link>
            <Link to="/request-demo" onClick={() => trackEvent('Contact CTA Clicked', {
            location: 'Permis Hero'
          })} className="inline-flex items-center justify-center gap-3 rounded-xl border border-[#E2E3EE] bg-white px-7 py-4 font-sans text-[17px] font-medium text-[#121A3A] transition-colors duration-150 hover:border-[#C9C5F5] whitespace-nowrap" data-id="element-5096" data-mp="3uwfp14nodiln">
              {pick(permisCopy.secondary)}
              <ArrowRightIcon className="w-5 h-5" strokeWidth={2} aria-hidden="true" data-id="element-5097" data-mp="271ixaci662lv" />
            </Link>
          </div>
          <p className="mt-8 flex flex-wrap items-center gap-x-3 gap-y-1 font-sans text-[15px] text-[#6B7180]" data-id="element-5098" data-mp="2g04n8spz0jwo">
            {caps.map((c, i) => <Fragment key={c} data-id="element-5099" data-mp="1sbbqiu1iy810" data-mpi={i}>
                {i > 0 && <span aria-hidden="true" data-id="element-5100" data-mp="1nbgz8pdu7gff">·</span>}
                <span data-id="element-5101" data-mp="1ncs1gkrmwg4f">{c}</span>
              </Fragment>)}
          </p>
        </motion.div>

        {/* Right — desktop composition */}
        <motion.div initial={{
        opacity: 0,
        y: 14
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        delay: 0.06,
        ease: EASE
      }} className="hidden xl:block relative h-[760px]" data-id="element-5102" data-mp="1x9mzz3nwqgip">
          <img src={PERMIS_IMAGES.house} alt="" className="absolute left-[8%] right-0 top-0 h-[600px] w-[92%] object-cover" style={{
          WebkitMaskImage: 'radial-gradient(ellipse 72% 70% at 55% 42%, #000 50%, transparent 100%)',
          maskImage: 'radial-gradient(ellipse 72% 70% at 55% 42%, #000 50%, transparent 100%)'
        }} data-id="element-5103" data-mp="14lr23vmlqikc" />
          <div className="absolute inset-0 bg-white/15" aria-hidden="true" data-id="element-5104" data-mp="llsg516zxjag" />
          <div className="absolute left-0 top-[3%] w-[370px] z-20" data-id="element-5105" data-mp="qkvclx8wuh6t">
            <QuestionCard isFR={isFR} data-id="element-5106" data-mp="vj6mg7z7grp1" />
          </div>
          <div className="absolute left-[1.5%] top-[17%] w-[320px] z-20" data-id="element-5107" data-mp="vhvlefx59y1m">
            <ProcessSteps isFR={isFR} data-id="element-5108" data-mp="10i02a71ysusd" />
          </div>
          <div className="absolute right-0 top-[1%] w-[430px] z-20" data-id="element-5109" data-mp="10gf5sbw3b1rv">
            <PropertyCard isFR={isFR} data-id="element-5110" data-mp="14jbgzhjavvls" />
          </div>
          <div className="absolute right-0 bottom-[8%] w-[500px] z-10" data-id="element-5111" data-mp="329wb38wqzqjg">
            <PermisDashboard isFR={isFR} data-id="element-5112" data-mp="32bzagjjsiw0u" />
          </div>
          <div className="absolute left-[33%] bottom-0 z-20" data-id="element-5113" data-mp="36bzo41z2f36u">
            <PermisPhone isFR={isFR} data-id="element-5114" data-mp="2yrfcidcri4ss" />
          </div>
          <div className="absolute left-[60%] bottom-[1%] w-[330px] z-30" data-id="element-5115" data-mp="2yta05zff4b0k">
            <VerifiedCard isFR={isFR} data-id="element-5116" data-mp="2tzxjqckw7aiw" />
          </div>
        </motion.div>

        {/* Right — tablet / mobile stack */}
        <div className="xl:hidden space-y-4" data-id="element-5117" data-mp="2p2fd4sucl7e2">
          <QuestionCard isFR={isFR} data-id="element-5118" data-mp="2l0kb1m4lepz8" />
          <PropertyCard isFR={isFR} data-id="element-5119" data-mp="2l1lk5l21t2eb" />
          <div className="relative rounded-3xl overflow-hidden" data-id="element-5120" data-mp="2kyhbobhjvkhu">
            <img src={PERMIS_IMAGES.house} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" data-id="element-5121" data-mp="3hq1dls8a3vwp" />
            <div className="relative p-4 sm:p-6" data-id="element-5122" data-mp="3cpdmz7nyph30">
              <ProcessSteps isFR={isFR} data-id="element-5123" data-mp="38p3hdw430soo" />
            </div>
          </div>
          <div className="flex flex-col items-center gap-4 pt-2" data-id="element-5124" data-mp="33qs6di21wc4v">
            <PermisPhone isFR={isFR} data-id="element-5125" data-mp="33qiji3cayl3e" />
            <VerifiedCard isFR={isFR} data-id="element-5126" data-mp="1xaga5mish4bx" />
          </div>
          <PermisDashboard isFR={isFR} data-id="element-5127" data-mp="1xc11z4dpy963" />
        </div>
      </div>

      {/* Benefits */}
      <ul className="relative mt-10 pt-8 border-t border-[#EEEEF4] grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-0 lg:divide-x divide-[#EEEEF4]" data-id="element-5128" data-mp="1sa1yluhzbc95">
        {permisBenefits.map(b => {
        const Icon = BENEFIT_ICONS[b.key];
        return <li key={b.key} className="flex items-start gap-4 lg:px-8 first:lg:pl-0" data-id="element-5129" data-mp="1ksndnpjydnsx">
              <span className="w-16 h-16 rounded-full bg-[#F1F0FD] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-5130" data-mp="1otflced6fkn3">
                <Icon className="w-7 h-7 text-[#4F46E5]" strokeWidth={1.6} data-id="element-5131" data-mp="1b19drgo3alz9" />
              </span>
              <span className="min-w-0 pt-1" data-id="element-5132" data-mp="1fu2ii4jk5ubc">
                <span className="block font-sans text-[16.5px] font-semibold text-[#121A3A]" data-id="element-5133" data-mp="1fuu7gwingtsn">{pick(b.title)}</span>
                <span className="block font-sans text-[14.5px] text-[#5B6478] leading-snug mt-1" data-id="element-5134" data-mp="162y71et3crl9">{pick(b.body)}</span>
              </span>
            </li>;
      })}
      </ul>
    </div>;
}